import classNames from 'clsx';

import type { InputHTMLAttributes, ReactNode } from 'react';

import styles from './ToggleSwitch.module.css';

export interface ToggleSwitchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'style' | 'className'> {
  /** The label for the toggle switch. */
  label: string;
  /** Boolean value if label should be visible. */
  labelVisible?: boolean;
  /** Selection for label placement compared to toggle switch. */
  labelPosition?: 'before' | 'after';
  /** Boolean value to be used if the toggle mutates. If true the toggle is disabled in a loading state. */
  loading?: boolean;
  /** Boolean value if extra gap is needed on left of component. */
  extraLeftGap?: boolean;
}

function ConditionalWrapper({
  condition,
  wrapper,
  children,
}: {
  condition: boolean;
  wrapper: (children: ReactNode) => ReactNode;
  children: ReactNode;
}) {
  return condition ? wrapper(children) : children;
}

/** Represents a component for a toggle switch. */
export function ToggleSwitch({
  label,
  labelPosition = 'after',
  labelVisible = true,
  loading = false,
  extraLeftGap,
  ...restProps
}: ToggleSwitchProps) {
  const labelComponent = (
    <span
      className={classNames(
        { [styles['toggle-switch--visually-hidden']]: !labelVisible },
        { [styles['toggle-switch--disabled-label']]: restProps.disabled }
      )}
    >
      {label}
    </span>
  );

  return (
    <ConditionalWrapper
      condition={loading}
      wrapper={(children) => <div className={styles['toggle-switch--loading-cursor']}>{children}</div>}
    >
      <label
        className={classNames(
          styles['toggle-switch'],
          { [styles['toggle-switch--disabled']]: restProps.disabled },
          { [styles['toggle-switch--loading']]: loading },
          { [styles['toggle-switch--extra-left-gap']]: extraLeftGap }
        )}
      >
        {labelPosition === 'before' && labelComponent}
        <input className={styles['toggle-switch--visually-hidden']} {...restProps} type="checkbox" />
        <span className={styles['toggle-switch__visible']} title={!labelVisible ? label : ''} />
        {labelPosition === 'after' && labelComponent}
      </label>
    </ConditionalWrapper>
  );
}
